<!-- This component do update the Vuex Product Store -->

<template>
  <slot
    :form-id="formId"
    :id="id"
    :selling-plan="sellingPlanId"
    :selling-plans="sellingPlans"
    :set-selling-plan-id="setSellingPlanId"
    :has-subscription="hasSubscription"
    :is-active="isActive"
    :set-subscription-active="setSubscriptionActive"
    :quantity="quantity"
    :set-quantity="setQuantity"
    :increaseQuantityByOne="increaseQuantityByOne"
    :decreaseQuantityByOne="decreaseQuantityByOne"
    :selected-variant="selectedVariant"
    :find-variants-by-options="findVariantsByOptions"
    :is-active-option="isActiveOption"
    :is-visible-option="isVisibleOption"
    :is-available-option="isAvailableOption"
    :options="selectedOptions"
    :available="available"
    :addToCart="handleAddToCart"
    :loading="submitting || isLoading"
    :error="error"
    :add-error-message="addErrorMessage"
    :store-locator-url="storeLocatorUrl"
    :price="displayPrice"
    :compare-at-price="displayCompareAtPrice"
    :select-option="selectOption"
    :is-option-active="isOptionActive"
    :option-price="optionPrice"
    :option-compare-at-price="optionCompareAtPrice"
    :subscription-price="subscriptionPrice"
    :subscription-compare-at-price="subscriptionCompareAtPrice"
    :one-time-price="oneTimePrice"
    :one-time-compare-at-price="oneTimeCompareAtPrice"
    :selling-plan-options="sellingPlanOptions"
  />
</template>

<script>
export default {
  name: "ProductFormVuex",
};
</script>

<script setup>
import {ref, watch, onBeforeMount, computed} from "vue";
import {useStore} from "vuex";
import JSON5 from "json5";
import useProductForm from "@/vue/composables/useProductForm";
import {formatMoney} from "@shopify/theme-currency";
import {dispatchCartErrorToast} from "@/utils/cart-error-toast";

const formId = "" + Math.random(); // Please notice: useId might be the same between two different Vue instances, so random might work better.
const props = defineProps(["product"]);

const product = JSON5.parse(props.product);

const {
  // ProductInfo
  id,
  quantity,
  sellingPlanId,

  // Quantity
  setQuantity,
  increaseQuantityByOne,
  decreaseQuantityByOne,

  // Options
  selectedVariant,
  findVariantsByOptions,
  isActiveOption,
  isVisibleOption,
  isAvailableOption,
  selectedOptions,

  // Subscription
  hasSubscription,
  isActive,
  setSubscriptionActive,
  sellingPlans,
  setSellingPlanId,

  // Add To Cart
  available,
  error,
  isLoading,
  addToCart,

  // Price
  currentPrice,
  getPrice,
} = useProductForm({
  product,
});

const toPlanId = plan => {
  if (plan === undefined || plan === null || plan === "") return null;
  const num = parseInt(plan, 10);
  return Number.isFinite(num) ? num : null;
};

const selectOption = ({plan, qty}) => {
  if (isActive.value) {
    const planId = toPlanId(plan);
    if (planId !== null) setSellingPlanId(planId);
  }
  if (qty) setQuantity(parseInt(qty, 10));
};

const isOptionActive = ({plan, qty}) => {
  const planId = toPlanId(plan);
  const qtyNum = parseInt(qty, 10) || 1;
  if (isActive.value) {
    return sellingPlanId.value === planId && quantity.value === qtyNum;
  }
  return quantity.value === qtyNum;
};

const optionPriceData = ({plan, qty}) => {
  const planId = isActive.value ? toPlanId(plan) : null;
  const qtyNum = parseInt(qty, 10) || 1;
  return getPrice(product, id.value, qtyNum, planId);
};

const optionPrice = opt => {
  const data = optionPriceData(opt);
  return data ? formatMoney(data.price) : "";
};

const optionCompareAtPrice = opt => {
  const data = optionPriceData(opt);
  return data?.compareAtPrice ? formatMoney(data.compareAtPrice) : "";
};

const firstPlanId = (() => {
  const groups = product.sellingPlanGroups || product.selling_plan_groups || [];
  for (const g of groups) {
    const plan = (g.selling_plans || [])[0];
    if (plan) return plan.id;
  }
  return null;
})();

const sellingPlanOptions = (() => {
  const out = [];
  const groups = product.sellingPlanGroups || product.selling_plan_groups || [];
  groups.forEach(group => {
    (group.selling_plans || []).forEach(plan => {
      out.push({id: plan.id, name: plan.name});
    });
  });
  return out;
})();

const activeSubscriptionPlanId = computed(() => {
  const id = toPlanId(sellingPlanId.value);
  return id ?? firstPlanId;
});

const subscriptionPrice = computed(() => {
  const planId = activeSubscriptionPlanId.value;
  if (!planId) return "";
  const data = getPrice(product, id.value, quantity.value, planId);
  return data ? formatMoney(data.price * quantity.value) : "";
});

const subscriptionCompareAtPrice = computed(() => {
  const planId = activeSubscriptionPlanId.value;
  if (!planId) return "";
  const data = getPrice(product, id.value, quantity.value, planId);
  return data?.compareAtPrice
    ? formatMoney(data.compareAtPrice * quantity.value)
    : "";
});

const oneTimePrice = computed(() => {
  const data = getPrice(product, id.value, quantity.value, null);
  return data ? formatMoney(data.price * quantity.value) : "";
});

const oneTimeCompareAtPrice = computed(() => {
  const data = getPrice(product, id.value, quantity.value, null);
  return data?.compareAtPrice
    ? formatMoney(data.compareAtPrice * quantity.value)
    : "";
});

const store = useStore();

const displayPrice = computed(() =>
  currentPrice.value ? formatMoney(currentPrice.value.price) : "",
);
const displayCompareAtPrice = computed(() =>
  currentPrice.value?.compareAtPrice
    ? formatMoney(currentPrice.value.compareAtPrice)
    : "",
);

// Data flows from component to store.
// If you need two-way data flowing, mind using v1.

const variantIndexOf = variant => {
  if (!variant) return 0;
  const idx = product.variants.findIndex(v => v.id === variant.id);
  return idx >= 0 ? idx : 0;
};

onBeforeMount(() => {
  store.dispatch("product/setVariant", selectedVariant.value);
  store.dispatch("product/setSelectedIndex", variantIndexOf(selectedVariant.value));
  store.dispatch("product/setQuantity", quantity.value);
  store.dispatch("product/setSellingPlan", sellingPlanId.value);
  store.dispatch("product/setPrice", {
    price: formatMoney(currentPrice.value.price),
    compareAtPrice:
      currentPrice.value?.compareAtPrice &&
      formatMoney(currentPrice.value.compareAtPrice),
  });
});

watch(selectedVariant, value => {
  store.dispatch("product/setVariant", value);
  store.dispatch("product/setSelectedIndex", variantIndexOf(value));
});

watch(quantity, value => {
  store.dispatch("product/setQuantity", value);
});

watch(currentPrice, value => {
  store.dispatch(
    "product/setPrice",
    value && {
      price: formatMoney(value.price),
      compareAtPrice:
        value?.compareAtPrice && formatMoney(value.compareAtPrice),
    },
  );
});

watch(sellingPlanId, value => {
  store.dispatch("product/setSellingPlan", value);
});

const storeLocatorUrl = "/pages/store-locator";
const submitting = ref(false);

const addErrorMessage = computed(() => {
  const e = error.value;
  if (!e) return "";
  return e.message || "Unable to add to cart.";
});

const handleAddToCart = async () => {
  if (!available.value) {
    window.location.href = storeLocatorUrl;
    return;
  }

  submitting.value = true;
  try {
    // `cart/addItem` already put the line in the cart, announced it and opened
    // the drawer before this resolves. Fetching the whole cart again here and
    // only then opening is what kept the drawer waiting on two round trips.
    await addToCart();

    if (error.value) {
      dispatchCartErrorToast(store, error.value);
      return;
    }
  } finally {
    submitting.value = false;
  }
};
</script>
